import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { NeedTenant } from "../hooks/useData";
import { GlassCard, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { API, tenantStore } from "../lib/api";
import { MessageSquare, Send, Sparkles, Brain, User } from "lucide-react";
import Markdown from "../components/Markdown";

const SUGGESTIONS = [
  "What is the biggest bottleneck in this process?",
  "Why is conformance below target?",
  "Which resource is overloaded?",
  "How can we reduce cycle time?",
];

function Inner() {
  const { datasetId, currentDataset } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { setMessages([]); sessionRef.current = null; }, [datasetId]);

  if (!datasetId) return <EmptyState icon={MessageSquare} title="No dataset selected" />;

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      let url = `${API}/ai/chat`;
      if (tenantStore.isSuper && tenantStore.id) url += `?tenant_id=${tenantStore.id}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("pm_token")}` },
        body: JSON.stringify({ dataset_id: datasetId, message: q, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.session_id) sessionRef.current = payload.session_id;
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the analyst engine." }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div className="pm-fade-up h-full flex flex-col" style={{ maxHeight: "calc(100vh - 160px)" }}>
      <div className="mb-4">
        <div className="text-xs font-semibold tracking-wider uppercase text-cyan-700/80 mb-1">Natural-Language Querying</div>
        <h1 className="text-2xl font-extrabold text-slate-900">AI Process Analyst</h1>
        <p className="text-sm text-slate-500">Ask anything about <span className="font-medium text-slate-700">{currentDataset?.name}</span> in plain English.</p>
      </div>

      <GlassCard className="flex-1 flex flex-col overflow-hidden min-h-[420px]">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-2xl pm-gradient flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20"><Sparkles className="w-7 h-7 text-white" /></div>
              <h3 className="font-bold text-slate-800">Ask the analyst</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5 max-w-sm">Grounded in your live mining results — bottlenecks, variants, conformance and resources.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid={`suggestion-${s.slice(0, 10)}`}
                    className="text-left text-sm p-3 rounded-xl bg-white/60 border border-white/60 hover:bg-white pm-transition text-slate-600">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}-${i}`}>
              {m.role === "assistant" && <div className="w-8 h-8 rounded-lg pm-gradient flex items-center justify-center shrink-0"><Brain className="w-4 h-4 text-white" /></div>}
              <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "pm-gradient text-white rounded-br-sm" : "bg-white/80 border border-cyan-100 text-slate-700 rounded-bl-sm shadow-sm"}`}>
                {m.role === "assistant"
                  ? (m.content ? <Markdown>{m.content}</Markdown> : <span className="pm-cursor">▋</span>)
                  : m.content}
                {m.role === "assistant" && m.content && streaming && i === messages.length - 1 && <span className="pm-cursor">▋</span>}
              </div>
              {m.role === "user" && <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-slate-500" /></div>}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-4 border-t border-white/50 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about this process…" data-testid="chat-input"
            className="flex-1 bg-white/70 border border-white/60 rounded-xl px-4 h-11 text-sm outline-none focus:ring-2 focus:ring-cyan-500/40" />
          <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="chat-send" className="h-11 w-11 p-0 rounded-xl pm-gradient text-white border-0"><Send className="w-4 h-4" /></Button>
        </div>
      </GlassCard>
    </div>
  );
}

export default function AIChat() { return <NeedTenant><Inner /></NeedTenant>; }
